'use client';
import { UnifiedIncidentCaptureAdmissionGate } from '@/components/compliance/incidents/UnifiedIncidentCaptureAdmissionGate';
import { isSettingsDeferredOwnerPath } from '@/lib/settings/settings-deferred-owner';

import { useSettingsDelegation } from '@/hooks/useSettingsDelegation';
import { useSettingsHiddenLeaves } from '@/hooks/useSettingsHiddenLeaves';
import { resolveSettingsAccess } from '@/lib/settings/settings-resolver';
import { isOwnNotificationPreferencePath } from '@/lib/settings/settings-access-roles';
import { isPayEventFactQueuePath } from '@/lib/payroll/pay-event-fact-integrity';

import { usePathname, useRouter } from 'next/navigation';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import { Card, CardContent, Button } from '@statura/ui';
import { ShieldOff } from 'lucide-react';
import { hasRouteRoleMapping, isRoleAllowedForPath } from '../lib/route-access';
import { useModules } from '@/contexts/module-context';

/**
 * Page-level role access gate.
 * Checks the current pathname against PATH_ROLE_ACCESS and shows
 * a "Not authorised" card if the user's role doesn't match.
 * Place INSIDE ModuleGate: plan-tier checked first, then role.
 */
export function RoleGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const legacy = <LegacyRoleGate>{children}</LegacyRoleGate>;
  return pathname === '/compliance/incidents/new' ? (
    <UnifiedIncidentCaptureAdmissionGate legacy={legacy} requireModuleAccess={false}>
      {children}
    </UnifiedIncidentCaptureAdmissionGate>
  ) : (
    legacy
  );
}

function LegacyRoleGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const userRole = useResolvedAuthUser()?.role;
  const { billingPlan } = useModules();
  // No role loaded yet — let AuthGuard handle this
  if (!userRole) return <>{children}</>;

  // No mapping found — deny by default so new routes cannot bypass RBAC
  if (!hasRouteRoleMapping(pathname)) {
    console.warn('[role-gate] blocked', {
      pathname,
      reason: 'missing_route_role_mapping',
      userRole,
    });
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center"
        data-testid="role-gate-missing-policy"
      >
        <Card className="max-w-md text-center">
          <CardContent>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-status-warning-bg mb-4 mt-4">
              <ShieldOff className="h-7 w-7 text-status-warning-fg" />
            </div>
            <h3 className="font-heading text-card-title text-text-heading">
              Access configuration missing
            </h3>
            <p className="mt-2 text-body text-text-muted max-w-xs mx-auto">
              This page has not been assigned a role access policy yet. Contact your administrator.
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button variant="secondary" onClick={() => router.back()}>
                Go back
              </Button>
              <Button variant="primary" onClick={() => router.push('/overview')}>
                Go to home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    (pathname === '/settings' || pathname.startsWith('/settings/')) &&
    !isSettingsDeferredOwnerPath(pathname)
  ) {
    if (isOwnNotificationPreferencePath(pathname) || isPayEventFactQueuePath(pathname)) {
      return resolveSettingsAccess({
        leafId: pathname,
        actor: { role: userRole },
        context: { billingPlan },
      }).canOpen ? (
        <>{children}</>
      ) : (
        <SettingsPermissionMessage />
      );
    }
    return (
      <SettingsDestinationGate pathname={pathname} role={userRole} billingPlan={billingPlan}>
        {children}
      </SettingsDestinationGate>
    );
  }

  if (isRoleAllowedForPath(pathname, userRole, billingPlan)) return <>{children}</>;

  console.warn('[role-gate] blocked', {
    pathname,
    reason: 'role_denied',
    userRole,
  });

  // Denied — show friendly message
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="role-gate-denied">
      <Card className="max-w-md text-center">
        <CardContent>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-status-danger-bg mb-4 mt-4">
            <ShieldOff className="h-7 w-7 text-red-400" />
          </div>
          <h3 className="font-serif text-section-title font-bold text-statura-ink">
            Not authorised
          </h3>
          <p className="mt-2 text-body text-text-muted max-w-xs mx-auto">
            Your role does not have access to this page. Contact your administrator if you believe
            this is an error.
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            <Button variant="secondary" onClick={() => router.back()}>
              Go back
            </Button>
            <Button variant="primary" onClick={() => router.push('/overview')}>
              Go to dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsPermissionMessage({ loading = false }: { loading?: boolean }) {
  return (
    <div className="p-6" role={loading ? 'status' : 'alert'}>
      {loading ? 'Checking settings access…' : 'Ask your organisation administrator'}
    </div>
  );
}
function SettingsDestinationGate({
  pathname,
  role,
  billingPlan,
  children,
}: {
  pathname: string;
  role: string;
  billingPlan: string | null | undefined;
  children: React.ReactNode;
}) {
  const delegation = useSettingsDelegation();
  const hidden = useSettingsHiddenLeaves();
  if (delegation.isPending || hidden.isPending) return <SettingsPermissionMessage loading />;
  if (delegation.isError || hidden.isError) return <SettingsPermissionMessage />;
  const access = resolveSettingsAccess({
    leafId: pathname,
    actor: { role },
    delegation: delegation.data,
    context: { billingPlan, hiddenHrefs: hidden.data },
  });
  return access.canOpen ? <>{children}</> : <SettingsPermissionMessage />;
}
